import React, {Fragment, useRef, useState} from 'react';
import Paper from '@material-ui/core/Paper';
import Draggable from 'react-draggable';
import {makeStyles} from '@material-ui/core/styles';
import classNames from 'classnames';
import HighlightBox from './HighLightBox';
import UndoIcon from '@material-ui/icons/Undo';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import StatusMenu from './StatusMenu';
import {dismissReason} from "./constants";
import Tooltip from '@material-ui/core/Tooltip';
import {addSpacer, restoreJurorToBox, swapJurorToOther, updateJuror} from '../../state/asyncActions';
import {useDispatch, useSelector} from 'react-redux';
import {getJurorDemographics, getJurorError} from '../../state/selectors';
import Alert from '@material-ui/lab/Alert';
import {ActionCreators} from '../../state/actions';
import {ActionCreators as MultiSelectActionCreators} from "../../state/multiSelect/actions";
import MultiSelectModes from "../../state/multiSelect/modes";
import {Button} from '@material-ui/core';
import Display from '../../components/RichTextEditor/Display';
import {CheckBoxOutlineBlank, CheckBoxOutlined, SpaceBarOutlined} from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import theme from "../../components/theme";
import {ActionCreators as EtherealActionCreators} from '../../state/ethereal/actions';
import {
    getDismissedBoxBounds,
    getHighlightedQuadrant,
    getJuryPoolBounds,
    isDismissedQuadrantVisible,
} from '../../state/ethereal/selectors';
import EmojiIndex from "./EmojiIndex";

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: 0,
        height: 135,
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        color: 'black',
        overflow: 'hidden',
        userSelect: 'none',
        background: 'white',
        borderBottom: "6px solid white",
        marginRight: "0.5em"
    },
    juror: {
        background: theme.palette.secondary.main,
        color: 'white',
    },
    dismissed: {
        background: theme.palette.shade.main,
    },
    empty: {
        background: theme.palette.shade.dark,
    },
    header: {
        padding: 2,
        paddingRight: 4,
        paddingLeft: 4,
    },
    idBox: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    },
    autoMarginLeft: {
        marginLeft: 'auto',
    },
    demographics: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        wordBreak: "break-word"
    },
    demographicsText: {
        fontSize: 14,
        color: theme.palette.text.hint
    },
    content: {
        padding: 8,
        paddingTop: 2,
        paddingBottom: 2,
        whiteSpace: 'pre-wrap',
        wordBreak: "break-word",
        fontSize: 14
    },
    emojiIndex: {
        backgroundColor: "white",
        padding: "3px 5px 2px 5px",
        borderRadius: "4px",
        fontSize: 12,
        position: "absolute",
        zIndex: 100,
        bottom: -3,
        right: 6,
        boxShadow: "2px 2px 5px -2px black"
    },
    handle: {
        cursor: 'pointer',
    },
    error: {
        flex: 0,
        position: 'absolute',
        bottom: 0,
        left: 0,
        zIndex: 1
    },
    errorContent: {
        color: '#f7f7f7',
    },
}));

const getBoundsFromRef = (ref) => {
    if (ref && ref.current) {
        return {
            left: ref.current.getBoundingClientRect().left,
            right: ref.current.getBoundingClientRect().right,
            top: ref.current.getBoundingClientRect().top,
            bottom: ref.current.getBoundingClientRect().bottom,
        }
    }
    return {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    }
};

const checkInside = (bounds, position) => {
    return (
        bounds &&
        position &&
        position.x > bounds.left &&
        position.x < bounds.right &&
        position.y > bounds.top &&
        position.y < bounds.bottom
    );
};

const checkQuad = (bounds, position) => {
    const quadWidth = (bounds.right - bounds.left) / 4;
    if (
        position.x > bounds.left &&
        position.x < quadWidth + bounds.left
    ) {
        return dismissReason.ProsecutionPerempt;
    } else if (
        position.x > quadWidth + bounds.left &&
        position.x < 2 * quadWidth + bounds.left
    ) {
        return dismissReason.DefensePerempt;
    } else if (
        position.x > 2 * quadWidth + bounds.left &&
        position.x < 3 * quadWidth + bounds.left
    ) {
        return dismissReason.ForCause;
    } else if (
        position.x > 3 * quadWidth + bounds.left &&
        position.x < bounds.right
    ) {
        return dismissReason.ForHardship
    } else {
        return 0;
    }
};

const findCenter = (bBox) => {
    return {
        x: (bBox.left + bBox.right) / 2,
        y: (bBox.top + bBox.bottom) / 2,
    };
};

const JurorError = ({caseId, juror, jurorError}) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    function handleClose(e) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(ActionCreators.removeJurorError(juror._id));
    }

    function handleRetry(e) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(updateJuror(caseId, juror._id, juror));
    }

    if (!jurorError) {
        return null;
    }

    return (
        <Alert key={`juror-error-${juror._id}`}
               className={classes.error}
               severity={'error'}
               onClose={handleClose}>
            {jurorError.error ? jurorError.error.toString() : 'An error occurred, please try again'}
            <Button color="inherit" size="small" onClick={handleRetry}>Retry</Button>
        </Alert>
    );
};

const JurorCard = ({
                       caseId,
                       demographicCategories,
                       showDemographics,
                       jurorId,
                       juror,
                       mode,
                       seatNumber,
                       spaceNumber,
                       type,
                       addEmptyJuror,
                       getEmptyJurors,
                       multiSelect
                   }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const jurorError = useSelector((state) => getJurorError(state, {jurorId: juror ? juror._id : 0}));
    const dismissedBoxBounds = useSelector(getDismissedBoxBounds);
    const juryPoolBounds = useSelector(getJuryPoolBounds);
    const dismissedQuadrantVisible = useSelector(isDismissedQuadrantVisible);
    const highlightedQuadrant = useSelector(getHighlightedQuadrant);

    const seatNumberAttributeName = mode === "jury-box" ? "seatNumber" : "alternateSeatNumber";

    const [cardState, setCardState] = useState({
        cardPosition: {x: 0, y: 0},
        cardZIndex: null,
        positionOffset: {x: 0, y: 0},
    });
    const [highlightBox, setHighlightBox] = useState({
        box: null,
        prevSeatNum: null,
    });
    const [highlightJuryPool, setHighlightJuryPool] = useState(null);
    const [showAdd, setShowAdd] = useState('hidden');

    const cardRef = useRef();

    React.useEffect(() => {
        setCardState({
            cardPosition: {x: 0, y: 0},
            cardZIndex: null,
            positionOffset: {x: 0, y: 0}
        })
    }, [jurorId]);

    function addSpacerRequested(e) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(addSpacer(caseId, spaceNumber));
    }

    const handleClick = async () => {
        //console.log("Handling click...");
        dispatch(EtherealActionCreators.setDismissedQuadrantVisible(false));
        setCardState({
            cardZIndex: null,
            cardPosition: {x: 0, y: 0},
            positionOffset: {x: 0, y: 0},
        });

        let tmpJurorInfo = {...juror};
        if (!tmpJurorInfo) {
            tmpJurorInfo = {
                [seatNumberAttributeName]: seatNumber,
            };
        }
        if (tmpJurorInfo[seatNumberAttributeName] === undefined) {
            tmpJurorInfo[seatNumberAttributeName] = seatNumber;
        }

        if (multiSelect && multiSelect.mode === MultiSelectModes.ON) {
            if (tmpJurorInfo._id) {
                if (multiSelect.selectedJurorIds.indexOf(tmpJurorInfo._id) === -1) {
                    dispatch(MultiSelectActionCreators.addJurorId(tmpJurorInfo._id));
                } else {
                    dispatch(MultiSelectActionCreators.removeJurorId(tmpJurorInfo._id));
                }
            }
        } else {
            let seatNumber = tmpJurorInfo[seatNumberAttributeName]
            if (type === "dismissed") {
                seatNumber = null;
            }
            dispatch(ActionCreators.openJurorModal(caseId, tmpJurorInfo._id, seatNumber, mode));
        }
    };

    const handleEmojiClick = (emoji, occurrence) => {
        dispatch(ActionCreators.openJurorModal(caseId, juror._id, seatNumber, mode, emoji, occurrence));
    }

    const checkInSeat = (position) => {
        const emptyJurors = getEmptyJurors();
        for (let seatNum of Object.keys(emptyJurors)) {
            if (
                checkInside(
                    emptyJurors[seatNum].current.getBoundingClientRect(),
                    position,
                )
            ) {
                return seatNum;
            }
        }
        return null;
    };

    const handleScroll = () => {
        let offset = 50;
        let limit = document.body.offsetHeight - window.innerHeight;
        if (
            cardRef.current.getBoundingClientRect().top < 0 &&
            cardState.positionOffset !== undefined &&
            window.pageYOffset > 0
        ) {
            if (offset - window.pageYOffset > 0) {
                offset = window.pageYOffset;
            }
            if (!dismissedQuadrantVisible) {
                dispatch(EtherealActionCreators.setDismissedQuadrantVisible(true));
            }
            setCardState({
                cardZIndex: 2000,
                position: cardState.position,
                positionOffset: {
                    x: 0,
                    y: -offset + cardState.positionOffset.y,
                },
            });
            window.scrollBy(0, -offset);
        }
        if (
            cardRef.current.getBoundingClientRect().bottom >
            window.innerHeight &&
            cardState.positionOffset !== undefined &&
            limit > window.pageYOffset
        ) {
            if (limit - window.pageYOffset < offset) {
                offset = limit - window.pageYOffset;
            }
            if (!dismissedQuadrantVisible) {
                dispatch(EtherealActionCreators.setDismissedQuadrantVisible(true));
            }
            setCardState({
                cardZIndex: 2000,
                position: cardState.position,
                positionOffset: {
                    x: 0,
                    y: offset + cardState.positionOffset.y,
                },
            });
            window.scrollBy(0, offset);
        }
    };

    const dragging = async (ev, positionA) => {
        //console.log("Dragging...");
        if (!dismissedQuadrantVisible) {
            dispatch(EtherealActionCreators.setDismissedQuadrantVisible(true));
            setCardState({
                cardZIndex: 2000,
                position: cardState.position,
                positionOffset: {x: 0, y: 0},
            });
        }

        let position = findCenter(
            cardRef.current.getBoundingClientRect(),
        );
        handleScroll();
        let newSeatNum = checkInSeat(position);
        if (
            newSeatNum !== null &&
            newSeatNum !== highlightBox.prevSeatNum
        ) {
            setHighlightBox({
                box: getEmptyJurors()[
                    newSeatNum
                    ].current.getBoundingClientRect(),
                prevSeatNum: newSeatNum,
            });
        } else if (
            highlightBox.prevSeatNum !== null &&
            newSeatNum === null
        ) {
            setHighlightBox({
                box: null,
                prevSeatNum: null,
            });
        }

        let insideQuad = checkInside(dismissedBoxBounds, position);
        if (insideQuad) {
            const matchedQuad = checkQuad(dismissedBoxBounds, position);
            if (matchedQuad !== highlightedQuadrant) {
                dispatch(EtherealActionCreators.setHighlightedQuadrant(matchedQuad));
            }
        } else if (!insideQuad) {
            if (highlightedQuadrant !== 0) {
                dispatch(EtherealActionCreators.setHighlightedQuadrant(0));
            }
        }
        if (checkInside(juryPoolBounds, position)) {
            setHighlightJuryPool(juryPoolBounds);
        } else {
            setHighlightJuryPool(null);
        }
    };

    const dragStop = (event, deltaPos) => {
        if (deltaPos.x === 0 && deltaPos.y === 0) {
            handleClick();
            return;
        }
        let position = findCenter(
            cardRef.current.getBoundingClientRect(),
        );
        let newSeatNum = checkInSeat(position);

        if (newSeatNum !== null) {
            dispatch(EtherealActionCreators.setDismissedQuadrantVisible(false));
            setCardState({
                cardZIndex: cardState.cardZIndex,
                position: deltaPos,
                positionOffset: {x: 0, y: cardState.positionOffset.y},
            });
            const newJurorInfo = {...juror, [seatNumberAttributeName]: parseInt(newSeatNum)};
            console.log("Sending juror to new seat: ", {
                mode,
                seatNumberAttributeName,
                newSeatNum
            });
            updateJurorInfo(newJurorInfo);
        } else if (checkInside(dismissedBoxBounds, position)) {
            dispatch(EtherealActionCreators.setDismissedQuadrantVisible(false));
            setCardState({
                cardZIndex: cardState.cardZIndex,
                position: deltaPos,
                positionOffset: {x: 0, y: cardState.positionOffset.y},
            });
            console.log("Dismissing juror...", {
                mode
            });
            dismiss(checkQuad(dismissedBoxBounds, position));
        } else if (checkInside(juryPoolBounds, position)) {
            dispatch(EtherealActionCreators.setDismissedQuadrantVisible(false));
            setCardState({
                cardZIndex: cardState.cardZIndex,
                position: deltaPos,
                positionOffset: {x: 0, y: cardState.positionOffset.y},
            });
            console.log("Sending to jury pool...", {
                mode
            });
            let newJurorInfo = {...juror, seatNumber: null, alternateSeatNumber: null};
            updateJurorInfo(newJurorInfo);
        } else {
            dispatch(EtherealActionCreators.setDismissedQuadrantVisible(false));
            setCardState({
                cardZIndex: null,
                cardPosition: {x: 0, y: 0},
                positionOffset: {x: 0, y: 0},
            });
            console.log("Resetting drag: ", {
                mode
            });
        }

        setHighlightBox({
            box: null,
            prevSeatNum: null
        });
        setHighlightJuryPool(null);
        dispatch(EtherealActionCreators.setHighlightedQuadrant(0));
    };

    const dismiss = (quadrant) => {
        let discardedAttributeName = mode === "jury-box" ? "discarded" : "alternateDiscarded";
        let newJurorProps = {
            [discardedAttributeName]: true,
        };
        if (quadrant === dismissReason.ProsecutionPerempt) {
            newJurorProps.discardedReason = 'peremptory';
            newJurorProps.discarder = 'prosecutor';
        } else if (quadrant === dismissReason.DefensePerempt) {
            newJurorProps.discardedReason = 'peremptory';
            newJurorProps.discarder = 'defense';
        } else if (quadrant === dismissReason.ForCause) {
            newJurorProps.discardedReason = 'cause';
        } else if (quadrant === dismissReason.ForHardship) {
            newJurorProps.discardedReason = 'hardship';
        }
        let newJurorInfo = {...juror, ...newJurorProps};
        newJurorInfo.updated = (new Date()).toISOString();
        updateJurorInfo(newJurorInfo);
    };

    const restoreJuror = (e) => {
        e.stopPropagation();
        dispatch(restoreJurorToBox(caseId, juror._id, mode));
    };

    const updateJurorInfo = (newJurorInfo) => {
        if (juror) {
            newJurorInfo._id = juror._id;
            dispatch(updateJuror(caseId, newJurorInfo._id, newJurorInfo));
        }
    };

    const updateStatus = (status) => {
        const newJurorInfo = {...juror, status: status};
        dispatch(updateJuror(caseId, juror._id, newJurorInfo));
    };

    const swapToOther = () => {
        dispatch(swapJurorToOther(caseId, juror._id, mode));
    }

    const getSymbol = () => {
        let symbol = '';
        if (juror.discarder === 'defense') {
            symbol = 'D';
        } else if (juror.discarder === 'prosecutor') {
            symbol = 'P';
        }
        if (juror.discardedReason === 'cause') {
            symbol = 'C';
        }
        if (juror.discardedReason === 'hardship') {
            symbol = 'H';
        }
        return symbol;
    };

    let className = '';
    let symbol = '';
    if (type === 'juror') {
        className = classNames(classes.header, classes.juror);
    } else {
        className = classNames(classes.header, classes.dismissed);
        symbol = getSymbol();
    }

    if (juror) {
        let demographicsItems = '';
        if (juror.demographics) {

            demographicsItems = getJurorDemographics(juror, demographicCategories);

            demographicsItems = demographicsItems.map((demographic, index) => {
                return (
                    <Fragment key={demographic.name + '-' + index}>
                        <Tooltip key={demographic.name} title={demographic.name}>
                            <span>
                              {demographic.value}
                            </span>
                        </Tooltip>
                        {index < demographicsItems.length - 1 && ", "}
                    </Fragment>
                );
            });
        }
        let jurorID = '';
        if (juror.hasOwnProperty('nameAndId')) {
            jurorID = juror.nameAndId;
        } else if (juror.id) {
            jurorID = juror.id;
        }

        if (juror.discarded || juror.alternateDiscarded) {
            return (
                // Dismissed juror card
                <div style={{position: "relative"}}>
                    <Paper
                        elevation={3}
                        className={classNames(classes.paper, classes.handle)}
                        onClick={handleClick}
                    >
                        <Box display="flex" p={1} className={className}>
                            <Box flexGrow={1} className={classNames(classes.header, classes.idBox)}>
                                <b>{jurorID}</b>
                            </Box>
                            <Box className={classes.header}>
                                {symbol}
                            </Box>
                            <Box className={classes.header}>
                                <Tooltip title="Restore Juror">
                                    <IconButton
                                        style={{padding: 0, color: 'black'}}
                                        onClick={(e) => restoreJuror(e)}
                                    >
                                        <UndoIcon fontSize='small'/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        {showDemographics &&
                            <Box className={classes.demographics}>
                                <Typography variant={"body1"}
                                            className={classes.demographicsText}>{demographicsItems}</Typography>
                            </Box>
                        }
                        <Box className={classes.content}>
                            <Display richTextStyles={juror.commentsStyles || []} text={juror.comments || ""}/>
                        </Box>
                        <JurorError caseId={caseId} juror={juror} jurorError={jurorError}/>
                    </Paper>
                    <EmojiIndex text={juror.comments || ""}
                                onClick={handleEmojiClick}
                                className={classes.emojiIndex}/>
                </div>
            );
        }

        let rightComponent = (
                <StatusMenu
                    updateStatus={updateStatus}
                    mode={mode}
                    onSwap={swapToOther}
                    onDismiss={dismiss}
                    status={juror.status ? juror.status : null}/>
            ),
            extraProps = {},
            cardStyles = {zIndex: cardState.cardZIndex, flex: 1, minWidth: 0};
        if (multiSelect && multiSelect.mode === MultiSelectModes.ON) {
            extraProps.onClick = handleClick;
            cardStyles.cursor = "pointer";
            if (multiSelect.selectedJurorIds.indexOf(juror._id) >= 0) {
                rightComponent = (
                    <CheckBoxOutlined style={{fontSize: 20, marginBottom: "-4px"}}/>
                );
            } else {
                rightComponent = (
                    <CheckBoxOutlineBlank style={{fontSize: 20, marginBottom: "-4px"}}/>
                );
            }
        }

        return (
            // Populated juror card
            <React.Fragment>
                <Draggable
                    onStop={dragStop}
                    onDrag={dragging}
                    position={cardState.cardPosition}
                    positionOffset={cardState.positionOffset}
                    handle="div.handle"
                    disabled={multiSelect && multiSelect.mode === MultiSelectModes.ON}
                >
                    <div
                        ref={cardRef}
                        style={cardStyles}
                        {...extraProps}
                    >
                        <Paper
                            elevation={3}
                            className={classes.paper}
                        >
                            <Box display="flex" p={1} className={className}>
                                <Box flexGrow={1} className={classNames(classes.header, classes.idBox)}>
                                    <div className={classNames(classes.handle, 'handle')}>
                                        <Box className={classes.idBox}>
                                            <b>{jurorID}</b>
                                        </Box>
                                    </div>
                                </Box>
                                <Box className={classes.header}>
                                    <div className={classNames(classes.handle, 'handle')}>
                                        <b>{seatNumber}</b>
                                    </div>
                                </Box>
                                <Box className={classes.header}>
                                    {rightComponent}
                                </Box>
                            </Box>
                            <div className={classNames(classes.handle, 'handle')} style={{flex: 1}}>
                                {showDemographics &&
                                    <Box className={classes.demographics}>
                                        <Typography variant={"body1"}
                                                    className={classes.demographicsText}>{demographicsItems}</Typography>
                                    </Box>
                                }
                                <Box className={classes.content}>
                                    <Display richTextStyles={juror.commentsStyles || []} text={juror.comments || ""}/>
                                </Box>
                            </div>
                            <JurorError caseId={caseId} juror={juror} jurorError={jurorError}/>
                        </Paper>
                        <EmojiIndex text={juror.comments || ""}
                                    onClick={handleEmojiClick}
                                    className={classes.emojiIndex}/>
                    </div>
                </Draggable>
                <HighlightBox boundingBox={highlightBox.box}/>
                <HighlightBox boundingBox={highlightJuryPool}/>
            </React.Fragment>
        );
    } else {
        addEmptyJuror(seatNumber, cardRef);
        return (
            // empty juror space
            <React.Fragment>
                <div ref={cardRef} style={{flex: 1}}>
                    <Box
                        className={classNames(classes.paper, classes.handle)}
                        onClick={handleClick}
                        borderRadius={5}
                        onMouseEnter={() => setShowAdd('')}
                        onMouseLeave={() => setShowAdd('hidden')}
                    >
                        <Box
                            display="flex"
                            className={classes.header}
                        >
                            <Box
                                className={classNames(
                                    classes.autoMarginLeft,
                                    classes.header,
                                )}
                            >
                                <b>{seatNumber}</b>
                            </Box>
                        </Box>
                        <Box
                            display="flex"
                            flexGrow={1}
                            flexDirection="row"
                            justifyContent="center"
                            alignItems="center"
                            position="relative"
                            style={{visibility: showAdd}}
                            p={2}
                        >

                            <Tooltip title={"Add juror"}>
                                <IconButton color='primary' size='small'>
                                    <AddCircleIcon style={{fontSize: 40}}/>
                                </IconButton>
                            </Tooltip>
                            <div style={{
                                position: 'absolute',
                                right: 10,
                                top: 0,
                                display: mode === "jury-box" ? "block" : "none"
                            }}>
                                <Tooltip title={"Add gap"}>
                                    <IconButton size='small' onClick={addSpacerRequested}>
                                        <SpaceBarOutlined/>
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </Box>
                    </Box>
                </div>
            </React.Fragment>
        );
    }
};

export default JurorCard;
